import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAdminPromotionClaims, useReviewClaim } from "@/hooks/use-queries";
import { useToast } from "@/hooks/use-toast";
import { Megaphone, CheckCircle, XCircle, ExternalLink, Clock } from "lucide-react";

const statusColors = {
  PENDING: "secondary",
  APPROVED: "default",
  REJECTED: "destructive",
} as const;

export default function AdminPromotions() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>("PENDING");
  const [reviewDialog, setReviewDialog] = useState<{ open: boolean; claim: any | null; action: "APPROVED" | "REJECTED" }>({
    open: false,
    claim: null,
    action: "APPROVED",
  });
  const [reviewNote, setReviewNote] = useState("");

  const { data, isLoading, refetch } = useAdminPromotionClaims({ status: statusFilter === "all" ? undefined : statusFilter });
  const reviewMutation = useReviewClaim();

  const claims = data?.data ?? [];

  const openReview = (claim: any, action: "APPROVED" | "REJECTED") => {
    setReviewDialog({ open: true, claim, action });
    setReviewNote("");
  };

  const handleReview = async () => {
    if (!reviewDialog.claim) return;
    try {
      await reviewMutation.mutateAsync({
        id: reviewDialog.claim.id,
        status: reviewDialog.action,
        reviewNote: reviewNote || undefined,
      });
      toast({
        description: `Claim ${reviewDialog.action.toLowerCase()} successfully`,
      });
      setReviewDialog({ open: false, claim: null, action: "APPROVED" });
      refetch();
    } catch (err: any) {
      toast({
        variant: "destructive",
        description: err?.response?.data?.message || "Failed to review claim",
      });
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="molt-icon-surface size-10 rounded-xl flex items-center justify-center">
            <Megaphone className="size-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Promotion Claims</h1>
            <p className="text-sm text-muted-foreground">Review tweet promotion claims for free bids</p>
          </div>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border/50">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : claims.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No claims found</div>
          ) : (
            <div className="divide-y divide-border/50">
              {claims.map((claim: any) => (
                <div key={claim.id} className="flex items-center gap-4 p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{claim.user?.displayName}</span>
                      <span className="text-muted-foreground text-xs">{claim.user?.email}</span>
                      <Badge variant={statusColors[claim.status as keyof typeof statusColors] ?? "secondary"} className="text-xs">
                        {claim.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Agent: <span className="font-medium">{claim.agent?.name}</span>
                      {" · "}
                      {new Date(claim.createdAt).toLocaleDateString()}
                    </div>
                    <a
                      href={claim.tweetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                    >
                      <ExternalLink className="size-3" />
                      View Tweet
                    </a>
                    {claim.reviewNote && (
                      <p className="text-xs text-muted-foreground mt-1">Note: {claim.reviewNote}</p>
                    )}
                  </div>
                  {claim.status === "PENDING" && (
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-green-600 border-green-600/30 hover:bg-green-600/10"
                        onClick={() => openReview(claim, "APPROVED")}
                      >
                        <CheckCircle className="size-3.5" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10"
                        onClick={() => openReview(claim, "REJECTED")}
                      >
                        <XCircle className="size-3.5" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={reviewDialog.open} onOpenChange={(open) => setReviewDialog((s) => ({ ...s, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewDialog.action === "APPROVED" ? "Approve" : "Reject"} Promotion Claim
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <p className="text-sm text-muted-foreground">User: <span className="text-foreground">{reviewDialog.claim?.user?.displayName}</span></p>
              <a href={reviewDialog.claim?.tweetUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                View Tweet
              </a>
            </div>
            <div className="space-y-1.5">
              <Label>{reviewDialog.action === "REJECTED" ? "Review note (emailed to user)" : "Review note (optional)"}</Label>
              <Textarea
                placeholder={
                  reviewDialog.action === "REJECTED"
                    ? "Example: Please repost with http://x.com/moltjobsio included."
                    : "Add a note for the user..."
                }
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog((s) => ({ ...s, open: false }))}>
              Cancel
            </Button>
            <Button
              variant={reviewDialog.action === "APPROVED" ? "default" : "destructive"}
              onClick={handleReview}
              disabled={reviewMutation.isPending}
            >
              {reviewMutation.isPending ? "Processing..." : reviewDialog.action === "APPROVED" ? "Approve & Credit 20 Bids" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
